import { type AppDispatch, type RootState } from '@/store/store';
import { setAssetClips, setSceneClips, upsertClip, removeClip, setAssetClipsLoading, selectClipsByAssetId, selectReactiveClipForAsset, updateClipTrigger } from './clipsSlice';
import type { ClipInstance, SupabaseClipType, AnimationStatus } from '@/types/clipTypes';
import { CLIP_CATEGORIES, isFadeSource, ANIMATION_STATUS_FILLED_DEFAULT } from '@/types/clipTypes';
import type { AssetInstance } from '@/types/assetTypes';
import { extendAssetEnd } from '@/utils/assetExtension';
import { createClipInstance as buildClipInstanceRow } from '@/utils/dataConverters';
import type { ClipService } from '@/modules/databaseService/types';
import { selectUIElementById } from '@/features/uiElements/uiElementsSlice';

import type { WriteScope } from '@/modules/undoRedo';
const parseClipSettings = (raw: unknown): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as Record<string, any>; } catch { return {}; }
  }
  if (typeof raw === 'object') return raw as Record<string, any>;
  return {};
};

export const fetchClipsByAssetId = (
  assetInstanceId: string,
  sceneInstanceId: string,
  services: { clip: ClipService }
) => async (dispatch: AppDispatch) => {
  dispatch(setAssetClipsLoading({ assetInstanceId, loading: true }));
  try {
    // Fetch all clips for the scene
    const supabaseClips = await services.clip.fetchAllSceneClips(sceneInstanceId);
    
    // Filter to this asset and convert to ClipInstance format
    const clips: ClipInstance[] = supabaseClips
      .filter((row: any) => row.asset_instance_id === assetInstanceId)
      .map((row: any) => ({
        sceneInstanceId: row.scene_instance_id,
        assetId: row.asset_instance_id ?? '',
        uiElementId: row.ui_element_instance_id ?? null,
        typeId: row.clip_type_id,
        instanceId: row.clip_instance_id,
        instanceStartTime: row.clip_instance_begin_time,
        instanceEndTime: row.clip_instance_end_time,
        instanceSourceName: row.clip_instance_source_name,
        instanceDisplayName: row.clip_instance_display_name,
        instanceDuration: row.clip_instance_duration,
      instanceSpeed: row.clip_instance_speed ?? 1.0,
      // Only set volume for audio clips, default to 1.00 if null/undefined (matches convertToClipInstance)
      ...(row.clip_instance_category === CLIP_CATEGORIES.AUDIO && {
        instanceVolume: row.clip_instance_volume ?? 1.00
      }),
      instanceCategory: row.clip_instance_category,
        clipInstanceSettings: row.clip_instance_settings,
        triggerAssetInstanceId: row.trigger_asset_instance_id ?? null,
        rotationDirection: row.rotation_direction,
        scaleTo: row.scale_to,
        height: row.height,
      }));

    dispatch(setAssetClips({ assetInstanceId, clips }));
  } catch (error) {
    console.error('Error fetching clips for asset:', error);
    dispatch(setAssetClips({ assetInstanceId, clips: [] }));
  }
};

// Rejects when the read fails, leaving the clips the editor holds as they were: an empty list would read as a scene with none
export const fetchClipsBySceneId = (
  sceneInstanceId: string,
  services: { clip: ClipService }
) => async (dispatch: AppDispatch) => {
  const supabaseClips = await services.clip.fetchAllSceneClips(sceneInstanceId);
  
  // Convert to ClipInstance format
  const clips: ClipInstance[] = supabaseClips.map((row: any) => ({
    sceneInstanceId: row.scene_instance_id,
    assetId: row.asset_instance_id ?? '',
    uiElementId: row.ui_element_instance_id ?? null,
    typeId: row.clip_type_id,
    instanceId: row.clip_instance_id,
    instanceStartTime: row.clip_instance_begin_time,
    instanceEndTime: row.clip_instance_end_time,
    instanceSourceName: row.clip_instance_source_name,
    instanceDisplayName: row.clip_instance_display_name,
    instanceDuration: row.clip_instance_duration,
    instanceSpeed: row.clip_instance_speed ?? 1.0,
    instanceVolume: row.clip_instance_volume,
    instanceCategory: row.clip_instance_category,
    clipInstanceSettings: row.clip_instance_settings,
    triggerAssetInstanceId: row.trigger_asset_instance_id ?? null,
    rotationDirection: row.rotation_direction,
    scaleTo: row.scale_to,
    height: row.height,
  }));

  dispatch(setSceneClips({ sceneInstanceId, clips }));
};

export const createClipInstance = (
  clipData: any,
  services: { clip: ClipService },
  scope?: WriteScope
) => async (dispatch: AppDispatch): Promise<{ clipInstance: ClipInstance; dbRow: any } | null> => {
  try {
    const newClipRow = await services.clip.addClipInstanceRow(clipData, scope);

    const clipInstance: ClipInstance = {
      sceneInstanceId: newClipRow.scene_instance_id,
      assetId: newClipRow.asset_instance_id ?? '',
      uiElementId: newClipRow.ui_element_instance_id ?? null,
      typeId: newClipRow.clip_type_id,
      instanceId: newClipRow.clip_instance_id,
      instanceStartTime: newClipRow.clip_instance_begin_time,
      instanceEndTime: newClipRow.clip_instance_end_time,
      instanceSourceName: newClipRow.clip_instance_source_name,
      instanceDisplayName: newClipRow.clip_instance_display_name,
      instanceDuration: newClipRow.clip_instance_duration,
      instanceSpeed: newClipRow.clip_instance_speed ?? 1.0,
      instanceVolume: newClipRow.clip_instance_volume,
      instanceCategory: newClipRow.clip_instance_category,
      clipInstanceSettings: newClipRow.clip_instance_settings,
      triggerAssetInstanceId: newClipRow.trigger_asset_instance_id ?? null,
      rotationDirection: newClipRow.rotation_direction,
      scaleTo: newClipRow.scale_to,
      height: newClipRow.height,
    };

    dispatch(upsertClip(clipInstance));

    return { clipInstance, dbRow: newClipRow };
  } catch (error) {
    console.error('Error creating clip instance:', error);
    return null;
  }
};

export const createReactiveClip = (
  sceneInstanceId: string,
  assetInstanceId: string,
  clipType: SupabaseClipType,
  triggerId: string,
  beginTime: number,
  services: { clip: ClipService },
  status: AnimationStatus = ANIMATION_STATUS_FILLED_DEFAULT,
  isUiElement: boolean = false,
  scope?: WriteScope
) => async (dispatch: AppDispatch): Promise<ClipInstance | null> => {
  const row = buildClipInstanceRow(sceneInstanceId, assetInstanceId, clipType, beginTime, clipType.clip_type_duration ?? 0);
  row.trigger_asset_instance_id = triggerId;
  if (isUiElement) { row.ui_element_instance_id = assetInstanceId; row.asset_instance_id = null; }
  const settings = parseClipSettings(row.clip_instance_settings);
  row.clip_instance_settings = { ...settings, animationStatus: status };
  const result = await dispatch(createClipInstance(row, services, scope));
  return result?.clipInstance ?? null;
};

// Binds a gate target with an await (Freeze) clip or unbinds whatever clip it has (membership is the gate's settings); false when it can't bind, rejects when an unbound clip won't go
export const setAssetAwaitReactive = (
  gateId: string,
  assetInstanceId: string,
  adding: boolean,
  services: { clip: ClipService },
  scope?: WriteScope
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
  const existing = selectReactiveClipForAsset(getState(), gateId, assetInstanceId);
  const isUiElement = !!selectUIElementById(getState(), assetInstanceId);
  // Audio/video react via their own hidden media clip (it holds at the gate and resumes from sourceIn on fire), so bind/clear that clip's FK instead of a Freeze clip.
  const mediaClip = selectClipsByAssetId(getState(), assetInstanceId).find(
    (c) => c.instanceCategory === CLIP_CATEGORIES.AUDIO || c.instanceCategory === CLIP_CATEGORIES.VIDEO
  );
  if (adding) {
    if (existing) return true;
    if (mediaClip) {
      dispatch(updateClipTrigger({ clipInstanceId: mediaClip.instanceId, triggerAssetInstanceId: gateId }));
      await services.clip.updateClipInstanceTrigger(mediaClip.instanceId, gateId, scope);
      return true;
    }
    const gate = getState().triggers.entities[gateId];
    if (!gate) return false;
    const awaitType = await services.clip.fetchAwaitClipType();
    if (!awaitType) return false;
    const created = await dispatch(createReactiveClip(gate.sceneInstanceId, assetInstanceId, awaitType, gateId, gate.time, services, 'Freeze', isUiElement, scope));
    return !!created;
  }
  if (existing) {
    if (existing.instanceCategory === CLIP_CATEGORIES.AUDIO || existing.instanceCategory === CLIP_CATEGORIES.VIDEO) {
      dispatch(updateClipTrigger({ clipInstanceId: existing.instanceId, triggerAssetInstanceId: null }));
      await services.clip.updateClipInstanceTrigger(existing.instanceId, null, scope);
    } else if (!await dispatch(deleteClipInstance(existing.instanceId, existing.assetId, existing.sceneInstanceId, services, scope))) {
      throw new Error('the reactive clip could not be deleted');
    }
  }
  return true;
};

/** Shows `changes` on the clip as the store holds it now, before its save, so the answer never undoes a later edit. */
const showClipChange = (dispatch: AppDispatch, getState: () => RootState, clipId: string, changes: Partial<ClipInstance>): void => {
  const existingClip = getState().clips.entities[clipId];
  if (existingClip) dispatch(upsertClip({ ...existingClip, ...changes }));
};

// Shown before it saves; rejects when the write fails, so a caller never takes it as saved
export const updateClipInstanceTime = (
  clipId: string,
  beginTime: number,
  endTime: number,
  speed: number | undefined,
  services: { clip: ClipService },
  scope?: WriteScope
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  showClipChange(dispatch, getState, clipId, {
    instanceStartTime: beginTime,
    instanceEndTime: endTime,
    instanceDuration: Math.max(0, endTime - beginTime),
    ...(speed !== undefined && { instanceSpeed: speed }),
  });
  await services.clip.updateClipInstanceTime(clipId, beginTime, endTime, speed, scope);
};

// A clip's window and settings in one write (a media trim: its times and where in the source it starts); shown before it saves, rejects when it fails
export const updateClipInstanceTimeAndSettings = (
  clipId: string,
  beginTime: number,
  endTime: number,
  speed: number | undefined,
  settings: Record<string, unknown>,
  services: { clip: ClipService },
  scope?: WriteScope
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  showClipChange(dispatch, getState, clipId, {
    instanceStartTime: beginTime,
    instanceEndTime: endTime,
    instanceDuration: Math.max(0, endTime - beginTime),
    ...(speed !== undefined && { instanceSpeed: speed }),
    clipInstanceSettings: settings,
  });
  await services.clip.updateClipInstanceTimeAndSettings(clipId, beginTime, endTime, speed, settings, scope);
};

// Push clips straddling the gate line to start at the gate (clear of the
// reactive clip), extending the asset; returns the new max end for the caller.
export const pushClipsClearOfReactiveGate = (
  gateId: string,
  assetId: string,
  services: any,
  gapTime = 0,
  scope?: WriteScope
) => (dispatch: AppDispatch, getState: () => RootState): number => {
  const state = getState();
  const gate = state.triggers.entities[gateId];
  const asset = state.sceneAssets.sceneAssetsArray.find((a: AssetInstance) => a.instanceId === assetId);
  if (!gate || !asset) return 0;
  const gateTime = gate.time;
  const crossing = selectClipsByAssetId(state, assetId).filter((c: ClipInstance) =>
    c.instanceCategory !== CLIP_CATEGORIES.SNAP
    && c.instanceCategory !== CLIP_CATEGORIES.AUDIO
    && c.instanceCategory !== CLIP_CATEGORIES.VIDEO
    && !isFadeSource(c.instanceSourceName)
    && c.instanceStartTime < gateTime
    && c.instanceEndTime > gateTime
  );
  if (crossing.length === 0) return 0;

  // Start clips clear of (to the right of) the reactive clip, with a gap.
  const newStart = gateTime + gapTime;
  let maxEnd = asset.instanceEndTime;
  for (const c of crossing) {
    const newEnd = newStart + (c.instanceEndTime - c.instanceStartTime);
    if (newEnd > maxEnd) maxEnd = newEnd;
    // Optimistic Redux move for instant feedback; the thunk then persists to DB, a failure told and put back by the failed-write handling
    dispatch(upsertClip({ ...c, instanceStartTime: newStart, instanceEndTime: newEnd, instanceDuration: newEnd - newStart }));
    void dispatch(updateClipInstanceTime(c.instanceId, newStart, newEnd, c.instanceSpeed, services, scope)).catch(() => undefined);
  }
  if (maxEnd > asset.instanceEndTime) {
    extendAssetEnd({
      assetInstanceId: assetId,
      newEndTime: maxEnd,
      dispatch,
      services,
      getState,
      scope,
    });
  }
  return maxEnd;
};

// Shown before it saves; rejects when the write fails, so a caller never takes it as saved
export const updateClipInstanceSettings = (
  clipId: string,
  settings: Record<string, unknown>,
  services: { clip: ClipService },
  scope?: WriteScope
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  showClipChange(dispatch, getState, clipId, {
    clipInstanceSettings: settings,
    // Extract tween-specific properties if they exist in settings
    ...(settings.rotationDirection && { rotationDirection: settings.rotationDirection as any }),
    ...(settings.scaleTo && { scaleTo: settings.scaleTo as any }),
    ...(settings.height && { height: settings.height as number }),
  });
  await services.clip.updateClipInstanceSettings(clipId, settings, scope);
};

// Shown before it saves (it changes the speed alone); rejects when the write fails, so a caller never takes it as saved
export const updateClipInstanceSpeed = (
  clipId: string,
  speed: number,
  services: { clip: ClipService },
  scope?: WriteScope
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  showClipChange(dispatch, getState, clipId, { instanceSpeed: speed });
  await services.clip.updateClipInstanceSpeed(clipId, speed, scope);
};

// Shown from the saved row; rejects when the write fails, so a caller never takes it as saved
export const updateClipInstanceVolume = (
  clipId: string,
  volume: number,
  services: { clip: ClipService },
  scope?: WriteScope
) => async (dispatch: AppDispatch, getState: () => any): Promise<void> => {
  await services.clip.updateClipInstanceVolume(clipId, volume, scope);
  
  // Get existing clip from store to merge with update
  const state = getState();
  const existingClip = state.clips.entities[clipId];
  
  if (existingClip) {
    const updatedClip: ClipInstance = {
      ...existingClip,
      instanceVolume: volume,
    };
    dispatch(upsertClip(updatedClip));
  }
};

export const deleteClipInstance = (
  clipId: string,
  assetId: string,
  sceneInstanceId: string,
  services: { clip: ClipService },
  scope?: WriteScope
) => async (dispatch: AppDispatch): Promise<boolean> => {
  try {
    const success = await services.clip.deleteClipInstance(clipId, scope);
    if (success) {
      dispatch(removeClip({ instanceId: clipId, assetId, sceneInstanceId }));
    }
    return success;
  } catch (error) {
    console.error('Error deleting clip instance:', error);
    return false;
  }
};


